/**
 * Billing Section
 *
 * User billing and subscription management section.
 * Displays subscription status, allows plan changes, payment management,
 * and shows billing history. Refactored from BillingPage for use in AccountPage tabs.
 */

import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  CheckCircle2,
  CreditCard,
  History,
  Loader2,
  Sparkles,
  Tag,
  X,
} from "lucide-react";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { Input } from "../ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import { Badge } from "../ui/badge";
import {
  useAvailablePlans,
  useBillingHistory,
  useCancelSubscription,
  useCreateCheckoutSession,
  useCreatePortalSession,
  useReactivateSubscription,
  useRetryPayment,
  useSubscriptionStatus,
  useValidateDiscountCode,
} from "../../hooks/useBilling";
import type {
  SubscriptionPlan,
  SubscriptionStatus,
  ValidateDiscountResponse,
} from "../../types/billing";

/**
 * Get display variant for subscription status badge
 */
function getStatusBadgeVariant(
  status: SubscriptionStatus | null
): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "active":
      return "default";
    case "trialing":
      return "secondary";
    case "past_due":
    case "unpaid":
    case "incomplete":
      return "destructive";
    case "canceled":
    case "paused":
      return "outline";
    default:
      return "outline";
  }
}

/**
 * Format currency amount for display
 */
function formatCurrency(cents: number, currency: string = "usd"): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

/**
 * Format date for display
 */
function formatDate(dateString: string | null): string {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/**
 * Plan selection card component
 */
function PlanCard({
  plan,
  isCurrentPlan,
  onSelect,
  isLoading,
}: {
  plan: SubscriptionPlan;
  isCurrentPlan: boolean;
  onSelect: () => void;
  isLoading: boolean;
}) {
  return (
    <Card
      className={`p-6 ${
        isCurrentPlan ? "border-primary border-2" : ""
      } relative`}
    >
      {isCurrentPlan && (
        <Badge className="absolute -top-2 left-4" variant="default">
          Current Plan
        </Badge>
      )}
      <div className="space-y-4">
        <div>
          <h3 className="text-xl font-semibold">{plan.name}</h3>
          {plan.description && (
            <p className="text-muted-foreground text-sm mt-1">
              {plan.description}
            </p>
          )}
        </div>

        <div className="flex items-baseline gap-1">
          <span className="text-3xl font-bold">
            {formatCurrency(plan.price_cents, plan.currency)}
          </span>
          <span className="text-muted-foreground">/month</span>
        </div>

        {plan.trial_days > 0 && (
          <p className="text-sm text-muted-foreground">
            Includes {plan.trial_days}-day free trial
            {plan.trial_conversations > 0 &&
              ` with ${plan.trial_conversations} conversations`}
          </p>
        )}

        {plan.features && (
          <ul className="space-y-2">
            {Object.entries(plan.features).map(([key, value]) => (
              <li key={key} className="flex items-center gap-2 text-sm">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                <span>
                  {key
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (l) => l.toUpperCase())}
                  {value !== true && value !== null && `: ${value}`}
                </span>
              </li>
            ))}
          </ul>
        )}

        <Button
          className="w-full"
          variant={isCurrentPlan ? "outline" : "default"}
          disabled={isCurrentPlan || isLoading}
          onClick={onSelect}
        >
          {isCurrentPlan ? "Current Plan" : "Subscribe"}
        </Button>
      </div>
    </Card>
  );
}

export function BillingSection() {
  const [searchParams] = useSearchParams();
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [validatedDiscount, setValidatedDiscount] =
    useState<ValidateDiscountResponse | null>(null);
  const [discountError, setDiscountError] = useState<string | null>(null);

  // Check for success/cancel URL params from Stripe checkout
  useEffect(() => {
    if (searchParams.get("success") === "true") {
      setShowSuccessMessage(true);
      // Clear the URL params but keep the tab param
      const newParams = new URLSearchParams();
      newParams.set("tab", "billing");
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}?${newParams.toString()}`
      );
    }
  }, [searchParams]);

  // Auto-trigger checkout when redirected from homepage plan selection
  const autoCheckoutPlanId = searchParams.get("auto_checkout");
  useEffect(() => {
    if (autoCheckoutPlanId && !createCheckout.isPending) {
      // Clear the param from the URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("auto_checkout");
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}?${newParams.toString()}`
      );
      handleSubscribe(autoCheckoutPlanId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoCheckoutPlanId]);

  // Queries
  const {
    data: subscriptionStatus,
    isLoading: statusLoading,
    error: statusError,
  } = useSubscriptionStatus();
  const { data: plans, isLoading: plansLoading } = useAvailablePlans();
  const { data: billingHistory, isLoading: historyLoading } =
    useBillingHistory(20);

  // Mutations
  const createCheckout = useCreateCheckoutSession();
  const createPortal = useCreatePortalSession();
  const retryPayment = useRetryPayment();
  const cancelSubscription = useCancelSubscription();
  const reactivateSubscription = useReactivateSubscription();
  const validateDiscount = useValidateDiscountCode();

  const handleSubscribe = (
    planId: string,
    referralCode?: string,
    discountCodeToUse?: string
  ) => {
    createCheckout.mutate({
      plan_id: planId,
      success_url: `${window.location.origin}/account?tab=billing&success=true`,
      cancel_url: `${window.location.origin}/account?tab=billing&canceled=true`,
      referral_code: referralCode,
      discount_code: discountCodeToUse,
    });
  };

  const handleValidateDiscount = (planId: string) => {
    if (!discountCode.trim()) {
      setDiscountError("Please enter a discount code");
      return;
    }

    setDiscountError(null);
    validateDiscount.mutate(
      { code: discountCode.trim(), plan_id: planId },
      {
        onSuccess: (data) => {
          if (data.valid) {
            setValidatedDiscount(data);
            setDiscountError(null);
          } else {
            setValidatedDiscount(null);
            setDiscountError(data.message || "Invalid discount code");
          }
        },
        onError: (error) => {
          setValidatedDiscount(null);
          setDiscountError(error.message || "Failed to validate discount code");
        },
      }
    );
  };

  const handleClearDiscount = () => {
    setDiscountCode("");
    setValidatedDiscount(null);
    setDiscountError(null);
  };

  const handleManageBilling = () => {
    createPortal.mutate({
      return_url: `${window.location.origin}/account?tab=billing`,
    });
  };

  const handleRetryPayment = () => {
    retryPayment.mutate();
  };

  const handleCancelSubscription = () => {
    cancelSubscription.mutate(true); // Cancel at period end
  };

  const handleReactivate = () => {
    reactivateSubscription.mutate();
  };

  if (statusLoading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="h-32 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  if (statusError) {
    return (
      <Card className="p-6 border-destructive">
        <div className="flex items-center gap-2 text-destructive">
          <AlertCircle className="h-5 w-5" />
          <span>Failed to load billing information. Please try again.</span>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Success Message */}
      {showSuccessMessage && (
        <Card className="p-4 bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800">
          <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
            <CheckCircle2 className="h-5 w-5" />
            <span className="font-medium">
              Subscription activated successfully! Thank you for subscribing.
            </span>
          </div>
        </Card>
      )}

      {/* Payment Issue Alert */}
      {subscriptionStatus?.has_subscription &&
        !subscriptionStatus.can_use_features &&
        subscriptionStatus.status !== "canceled" && (
          <Card className="p-4 bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-yellow-700 dark:text-yellow-400">
                <AlertCircle className="h-5 w-5" />
                <span>{subscriptionStatus.reason}</span>
              </div>
              {["past_due", "unpaid", "incomplete"].includes(
                subscriptionStatus.status || ""
              ) && (
                <Button
                  size="sm"
                  onClick={handleRetryPayment}
                  disabled={retryPayment.isPending}
                >
                  {retryPayment.isPending ? "Retrying..." : "Retry Payment"}
                </Button>
              )}
            </div>
          </Card>
        )}

      {/* Current Subscription Status */}
      <Card className="p-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Sparkles className="h-6 w-6 text-primary" />
              <h2 className="text-xl font-semibold">Current Subscription</h2>
            </div>

            {subscriptionStatus?.has_subscription ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="font-medium">
                    {subscriptionStatus.plan_name}
                  </span>
                  <Badge
                    variant={getStatusBadgeVariant(subscriptionStatus.status)}
                  >
                    {subscriptionStatus.status?.replace(/_/g, " ")}
                  </Badge>
                </div>

                {subscriptionStatus.is_trialing && (
                  <div className="space-y-1 text-sm text-muted-foreground">
                    <p>
                      Trial: {subscriptionStatus.trial_conversations_used} /{" "}
                      {subscriptionStatus.trial_conversations_limit}{" "}
                      conversations used
                      {subscriptionStatus.trial_end_date && (
                        <>
                          {" "}
                          • Ends {formatDate(subscriptionStatus.trial_end_date)}
                        </>
                      )}
                    </p>
                    {subscriptionStatus.trial_llm_cost_limit_usd !== null &&
                      subscriptionStatus.trial_llm_cost_used_usd !== null && (
                        <p>
                          LLM cost: $
                          {subscriptionStatus.trial_llm_cost_used_usd.toFixed(
                            2
                          )}{" "}
                          / $
                          {subscriptionStatus.trial_llm_cost_limit_usd.toFixed(
                            2
                          )}
                        </p>
                      )}
                    {subscriptionStatus.trial_transcription_cost_limit_usd !==
                      null &&
                      subscriptionStatus.trial_transcription_cost_used_usd !==
                        null && (
                        <p>
                          Transcription cost: $
                          {subscriptionStatus.trial_transcription_cost_used_usd.toFixed(
                            2
                          )}{" "}
                          / $
                          {subscriptionStatus.trial_transcription_cost_limit_usd.toFixed(
                            2
                          )}
                        </p>
                      )}
                    {subscriptionStatus.trial_total_cost_limit_usd !== null &&
                      subscriptionStatus.trial_total_cost_used_usd !== null && (
                        <p>
                          Total cost: $
                          {subscriptionStatus.trial_total_cost_used_usd.toFixed(
                            2
                          )}{" "}
                          / $
                          {subscriptionStatus.trial_total_cost_limit_usd.toFixed(
                            2
                          )}
                        </p>
                      )}
                  </div>
                )}

                {subscriptionStatus.cancel_at_period_end && (
                  <p className="text-sm text-yellow-600 dark:text-yellow-400">
                    Your subscription will end on{" "}
                    {formatDate(subscriptionStatus.current_period_end)}
                  </p>
                )}

                {!subscriptionStatus.cancel_at_period_end &&
                  subscriptionStatus.current_period_end && (
                    <p className="text-sm text-muted-foreground">
                      Next billing date:{" "}
                      {formatDate(subscriptionStatus.current_period_end)}
                    </p>
                  )}
              </div>
            ) : (
              <p className="text-muted-foreground">
                You don't have an active subscription. Choose a plan below to
                get started.
              </p>
            )}
          </div>

          {/* Quick Actions */}
          {subscriptionStatus?.has_subscription && (
            <div className="flex gap-2 flex-wrap">
              {subscriptionStatus.cancel_at_period_end ? (
                <Button
                  variant="default"
                  onClick={handleReactivate}
                  disabled={reactivateSubscription.isPending}
                >
                  {reactivateSubscription.isPending
                    ? "Reactivating..."
                    : "Reactivate"}
                </Button>
              ) : (
                <>
                  <Button variant="outline" onClick={handleManageBilling}>
                    <CreditCard className="h-4 w-4 mr-2" />
                    Manage Billing
                  </Button>

                  {subscriptionStatus.status !== "canceled" && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" className="text-destructive">
                          Cancel
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>
                            Cancel Subscription?
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            Your subscription will remain active until the end
                            of your current billing period. You can reactivate
                            at any time.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>
                            Keep Subscription
                          </AlertDialogCancel>
                          <AlertDialogAction
                            onClick={handleCancelSubscription}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            {cancelSubscription.isPending
                              ? "Canceling..."
                              : "Yes, Cancel"}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Available Plans */}
      {(!subscriptionStatus?.has_subscription ||
        subscriptionStatus?.status === "canceled") && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Available Plans</h2>

          {/* Discount Code Input */}
          {plans && plans.length > 0 && (
            <Card className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Tag className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">
                  Have a discount code?
                </span>
              </div>

              {validatedDiscount ? (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                      <div>
                        <p className="font-medium text-green-700 dark:text-green-400">
                          {validatedDiscount.name ||
                            `Code "${validatedDiscount.code}" applied`}
                        </p>
                        <p className="text-sm text-green-600 dark:text-green-500">
                          {validatedDiscount.discount_type === "percent_off"
                            ? `${validatedDiscount.percent_off}% off`
                            : `${formatCurrency(validatedDiscount.amount_off_cents || 0)} off`}
                          {validatedDiscount.duration === "once" &&
                            " (first month)"}
                          {validatedDiscount.duration === "repeating" &&
                            validatedDiscount.duration_months &&
                            ` (for ${validatedDiscount.duration_months} months)`}
                          {validatedDiscount.duration === "forever" &&
                            " (forever)"}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleClearDiscount}
                      className="h-8 w-8 p-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Price breakdown */}
                  <div className="mt-3 pt-3 border-t border-green-200 dark:border-green-800 space-y-1 text-sm">
                    <div className="flex justify-between text-muted-foreground">
                      <span>Original price:</span>
                      <span>
                        {formatCurrency(
                          validatedDiscount.original_amount_cents
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between text-green-600 dark:text-green-400">
                      <span>Discount:</span>
                      <span>
                        -
                        {formatCurrency(
                          validatedDiscount.discount_amount_cents
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between font-semibold">
                      <span>You pay:</span>
                      <span>
                        {formatCurrency(validatedDiscount.final_amount_cents)}
                        /month
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="Enter discount code"
                    value={discountCode}
                    onChange={(e) => {
                      setDiscountCode(e.target.value.toUpperCase());
                      setDiscountError(null);
                    }}
                    className="flex-1"
                    disabled={validateDiscount.isPending}
                  />
                  <Button
                    variant="outline"
                    onClick={() =>
                      plans.length > 0 && handleValidateDiscount(plans[0].id)
                    }
                    disabled={
                      validateDiscount.isPending || !discountCode.trim()
                    }
                  >
                    {validateDiscount.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Checking...
                      </>
                    ) : (
                      "Apply"
                    )}
                  </Button>
                </div>
              )}

              {discountError && (
                <p className="text-sm text-destructive mt-2 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {discountError}
                </p>
              )}
            </Card>
          )}

          {plansLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="p-6 animate-pulse">
                <div className="h-32 bg-muted rounded"></div>
              </Card>
            </div>
          ) : plans && plans.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {plans.map((plan) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  isCurrentPlan={false}
                  onSelect={() =>
                    handleSubscribe(plan.id, undefined, validatedDiscount?.code)
                  }
                  isLoading={createCheckout.isPending}
                />
              ))}
            </div>
          ) : (
            <Card className="p-6">
              <p className="text-muted-foreground">
                No subscription plans available at this time.
              </p>
            </Card>
          )}
        </div>
      )}

      {/* Billing History */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <History className="h-5 w-5" />
          <h2 className="text-xl font-semibold">Billing History</h2>
        </div>

        {historyLoading ? (
          <Card className="p-6 animate-pulse">
            <div className="space-y-3">
              <div className="h-4 bg-muted rounded w-full"></div>
              <div className="h-4 bg-muted rounded w-3/4"></div>
              <div className="h-4 bg-muted rounded w-1/2"></div>
            </div>
          </Card>
        ) : billingHistory && billingHistory.events.length > 0 ? (
          <Card className="divide-y">
            {billingHistory.events.map((event) => (
              <div
                key={event.id}
                className="p-4 flex items-center justify-between"
              >
                <div>
                  <p className="font-medium">{event.description}</p>
                  <p className="text-sm text-muted-foreground">
                    {formatDate(event.created_at)}
                  </p>
                </div>
                {event.amount_cents && event.amount_cents > 0 && (
                  <span
                    className={`font-medium ${
                      event.event_type === "refund_issued"
                        ? "text-green-600 dark:text-green-400"
                        : ""
                    }`}
                  >
                    {event.event_type === "refund_issued" ? "+" : ""}
                    {formatCurrency(
                      event.amount_cents,
                      event.currency || "usd"
                    )}
                  </span>
                )}
              </div>
            ))}
          </Card>
        ) : (
          <Card className="p-6">
            <p className="text-muted-foreground text-center">
              No billing history yet.
            </p>
          </Card>
        )}
      </div>
    </div>
  );
}
